<template>
  <div class="app-checkbox-wrap" :class="rootClasses">
    <label class="app-checkbox" :class="{ 'app-checkbox--disabled': disabled }">
      <input
        :id="inputId"
        ref="input"
        type="checkbox"
        v-bind="$attrs"
        :name="name"
        :checked="checked"
        :disabled="disabled"
        :required="required"
        :indeterminate.prop="indeterminate"
        :aria-invalid="!!error || null"
        :aria-describedby="describedBy"
        class="app-checkbox__input"
        @change="onChange"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      />
      <span class="app-checkbox__box">
        <i v-if="indeterminate" class="fa-solid fa-minus app-checkbox__icon"></i>
        <i v-else class="fa-solid fa-check app-checkbox__icon"></i>
      </span>

      <span class="app-checkbox__label">
        <slot>
          <span v-if="label" v-html="label"></span>
          <span v-if="showRequiredMark" class="app-checkbox__required" aria-hidden="true">*</span>
        </slot>
      </span>
    </label>

    <small
      v-if="error"
      :id="errorId"
      class="app-checkbox__error"
      v-html="error"
    ></small>
    <small
      v-else-if="help"
      :id="helpId"
      class="app-checkbox__help"
      v-html="help"
    ></small>
  </div>
</template>

<script>
let uid = 0

/**
 * App-styled checkbox — no-Bootstrap, drop-in upgrade over `c-checkbox`.
 *
 * Same v-model contract (`value` + `input` event). Additional features:
 *   - size variants (sm/md)
 *   - indeterminate state
 *   - error / help message slots (mirrors app-input)
 *   - required-mark in label
 *   - slot fallback for fully-custom label content
 *
 * Migration from c-checkbox: `value`, `label`, `help` carry over; new
 * `error`, `disabled`, `required`, `indeterminate`, `size`, `name`, `id`.
 */
export default {
  name: 'AppCheckbox',

  inheritAttrs: false,

  props: {
    value:        { type: Boolean, default: false },
    label:        { type: String,  default: '' },
    help:         { type: String,  default: '' },
    error:        { type: String,  default: '' },
    disabled:     { type: Boolean, default: false },
    required:     { type: Boolean, default: false },
    requiredMark: { type: Boolean, default: false },
    indeterminate:{ type: Boolean, default: false },
    name:         { type: String,  default: '' },
    id:           { type: String,  default: '' },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
  },

  emits: ['input', 'change', 'blur', 'focus'],

  data() {
    return {
      autoId: `app-checkbox-${++uid}`,
    }
  },

  computed: {
    checked()  { return !!this.value },
    inputId()  { return this.id || this.autoId },
    errorId()  { return `${this.inputId}-error` },
    helpId()   { return `${this.inputId}-help` },

    describedBy() {
      if (this.error) return this.errorId
      if (this.help) return this.helpId
      return null
    },

    showRequiredMark() {
      return this.requiredMark || this.required
    },

    rootClasses() {
      return [
        `app-checkbox-wrap--${this.size}`,
        {
          'app-checkbox-wrap--has-error': !!this.error,
          'app-checkbox-wrap--disabled': this.disabled,
        },
      ]
    },
  },

  methods: {
    onChange(e) {
      const next = e.target.checked
      this.$emit('input', next)
      this.$emit('change', next)
    },

    focus() { if (this.$refs.input) this.$refs.input.focus() },
  },
}
</script>

<style lang="scss" scoped>
$acb-text:        #2b3a4a;
$acb-text-muted:  #6c7a89;
$acb-text-subtle: #9aa6b3;
$acb-bg-surface:  #ffffff;
$acb-bg-soft:     #f6f8fa;
$acb-border:      #cfd6dc;
$acb-border-soft: rgba(0, 0, 0, 0.10);
$acb-blue:        #1f73c1;
$acb-blue-focus:  #4392d4;
$acb-red:         #d04848;
$acb-radius:      4px;
$acb-duration:    140ms;
$acb-ease:        cubic-bezier(.4, 0, .2, 1);

.app-checkbox-wrap {
  display: inline-flex;
  flex-direction: column;
  min-width: 0;

  &--disabled { pointer-events: none; opacity: 0.7; }
}

.app-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &--disabled { cursor: not-allowed; }

  // Visually-hidden native input (still focusable + keyboard-toggleable).
  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 1px;
    height: 1px;
    margin: -1px;
  }

  &__box {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: $acb-radius;
    background: $acb-bg-surface;
    border: 1px solid $acb-border;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    transition:
      background $acb-duration $acb-ease,
      border-color $acb-duration $acb-ease,
      box-shadow $acb-duration $acb-ease;
  }

  &__icon {
    font-size: 9px;
    line-height: 1;
    opacity: 0;
    transform: scale(0.6);
    transition: opacity $acb-duration $acb-ease, transform $acb-duration $acb-ease;
  }

  &__label {
    font-size: 13px;
    color: $acb-text;
    line-height: 1.4;
  }

  &__required {
    color: $acb-red;
    margin-left: 2px;
  }

  // ── Hover ──
  &:hover &__box {
    border-color: $acb-blue-focus;
  }

  // ── Checked ──
  &__input:checked + &__box {
    background: $acb-blue;
    border-color: $acb-blue;

    .app-checkbox__icon {
      opacity: 1;
      transform: scale(1);
    }
  }

  // ── Indeterminate ──
  &__input:indeterminate + &__box {
    background: $acb-blue;
    border-color: $acb-blue;

    .app-checkbox__icon {
      opacity: 1;
      transform: scale(1);
    }
  }

  // ── Focus ──
  &__input:focus-visible + &__box {
    box-shadow: 0 0 0 3px rgba(67, 146, 212, 0.20);
  }

  // ── Disabled ──
  &__input:disabled + &__box {
    background: $acb-bg-soft;
    border-color: $acb-border-soft;
  }
}

// ── Size variants ──
// `md` is the default (16px box, 13px label) and lives on the base
// selectors above. `sm`/`lg` are deltas from that baseline.
.app-checkbox-wrap--sm {
  .app-checkbox__box   { width: 14px; height: 14px; }
  .app-checkbox__icon  { font-size: 8px; }
  .app-checkbox__label { font-size: 12px; }
}

// Large variant — matches the 22px reminders checkbox in admin-dashboard
// (drawer + inline). Use for prominent form rows where the checkbox is
// the primary affordance, not a secondary inline toggle.
.app-checkbox-wrap--lg {
  .app-checkbox__box {
    width: 22px;
    height: 22px;
    border-width: 1.5px;
    border-radius: 6px;
  }
  .app-checkbox__icon  { font-size: 12px; }
  .app-checkbox__label { font-size: 14px; }

  // Slightly more breathing room between box and label at the larger size
  // (the default 8px gap reads cramped next to a 22px square).
  .app-checkbox { gap: 10px; }

  // Bigger focus ring scaled to the bigger box.
  .app-checkbox__input:focus-visible + .app-checkbox__box {
    box-shadow: 0 0 0 4px rgba(67, 146, 212, 0.22);
  }
}

// ── Error state ──
.app-checkbox-wrap--has-error {
  .app-checkbox__box { border-color: $acb-red; }
}

// ── Messages ──
.app-checkbox__error,
.app-checkbox__help {
  font-size: 11px;
  margin-top: 4px;
  margin-left: 24px;
  line-height: 1.4;
}
.app-checkbox__error { color: $acb-red; }
.app-checkbox__help  { color: $acb-text-muted; }
</style>
